import React, { useRef } from 'react'
import { Button, FormHelperText, IconButton, Input } from '@mui/material'
import { CloudUpload, Delete, Download, OpenInNew } from '@mui/icons-material'
import { Box } from '@mui/system'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import useShopQntmPdf from '../../hooks/use-qntm-pdf-upload'
import { useShopCartContext } from '../../context/use-shop-cart-context'
import { makeStyles } from 'tss-react/mui'

const MAX_FILE_SIZE = 5 * 1000 * 1000

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    maxWidth: 360,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: '1px dashed',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[100],
  },
  uploadContainer: {
    marginTop: theme.spacing(3),
  },
  fileButton: {
    maxWidth: '100%',
    whiteSpace: 'normal',
    textAlign: 'center',
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',

    '& .MuiButton-startIcon': {
      flexShrink: 0,
    },
  },
}))

/*
 * This component shows the upload input field at checkout for delivery pickup quantum.
 */
const ConfirmCheckoutDeliveryPickUpload: React.FC = () => {
  const { setCartData, cartData } = useShopCartContext()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { classes } = useStyles()
  const { addQntmContract, removeQntmContract, openQntmContract, isLoading } = useShopQntmPdf()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const fileName = cartData?.extensions?.quantum_shipping_contract?.fileName ?? ''
  const deliveryTemplateLink = cartData?.extensions?.quantum?.deliveryTemplateLink ?? ''
  const hasFile = Boolean(fileName)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueErrorStack(t('shop.delivery-pick-shipping-method.file-too-large'))
      return
    }

    addQntmContract(file)
      .then(result => {
        setCartData(result)

        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })
      .catch(error => {
        console.error(error)
        enqueueErrorStack(t('shop.delivery-pick-shipping-method.file-upload-error'))
      })
  }

  const handleRemoveFile = (): void => {
    removeQntmContract()
      .then(result => {
        setCartData(result)

        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })
      .catch(error => {
        console.error(error)
        enqueueErrorStack(t('shop.delivery-pick-shipping-method.file-remove-error'))
      })
  }

  const handleOpenFile = (): void => {
    openQntmContract()
      .then(fileUrl => {
        if (!fileUrl) {
          enqueueErrorStack(t('shop.delivery-pick-shipping-method.file-open-error'))
          return
        }

        const pdfBlob = new Blob([fileUrl], { type: 'application/pdf' })
        const pdfUrl = URL.createObjectURL(pdfBlob)
        window.open(pdfUrl, '_blank')
      })
      .catch(error => {
        console.error(error)
        enqueueErrorStack(t('shop.delivery-pick-shipping-method.file-open-error'))
      })
  }

  const handleDownloadTemplate = (): void => {
    if (!deliveryTemplateLink) {
      return
    }

    window.open(deliveryTemplateLink, '_blank')
  }

  return (
    <Box className={classes.container}>
      <Button variant="text" onClick={handleDownloadTemplate} startIcon={<Download />}>
        {t('shop.delivery-pick-shipping-method.download-template')}
      </Button>

      <Box className={classes.uploadContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            className={classes.fileButton}
            component="label"
            disabled={isLoading}
            variant={hasFile ? 'contained' : 'outlined'}
            startIcon={<CloudUpload />}
          >
            {fileName || t('shop.delivery-pick-shipping-method.upload-file-label')}

            <Input inputRef={inputRef} type="file" inputProps={{ accept: 'application/pdf' }} onChange={handleFileChange} sx={{ display: 'none' }} />
          </Button>

          {hasFile && (
            <>
              <IconButton disabled={isLoading} onClick={handleOpenFile}>
                <OpenInNew />
              </IconButton>

              <IconButton disabled={isLoading} onClick={handleRemoveFile}>
                <Delete />
              </IconButton>
            </>
          )}
        </Box>
        {!hasFile && <FormHelperText error>{t('shop.delivery-pick-shipping-method.file-is-required')}</FormHelperText>}
      </Box>
    </Box>
  )
}

export default ConfirmCheckoutDeliveryPickUpload
